import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api/v1`;

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: false,
  headers: { "Content-Type": "application/json" },
});

// attach bearer token from localStorage (platform CDN does not allow
// credentialed CORS, so we authenticate via Authorization header)
api.interceptors.request.use((config) => {
  const t = localStorage.getItem("access_token");
  if (t && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${t}`;
  }
  // ------------------------------------------------------------------
  // Platform scope injection — auto-append institution_id + campus_id
  // to every GET request when a super_admin has picked a scope in the
  // topbar. Endpoints that accept these params (finance, students,
  // fees, staff, attendance) will filter; others simply ignore extras.
  // Skip endpoints that shouldn't be scoped (auth, platform-level lists,
  // public endpoints, individual GET-by-id detail routes).
  // ------------------------------------------------------------------
  try {
    if ((config.method || "get").toLowerCase() === "get") {
      const raw = localStorage.getItem("edukampus.platform_scope");
      if (raw) {
        const scope = JSON.parse(raw);
        const url = String(config.url || "");
        const skip = /(^\/auth|^\/public|^\/tenants(\/|$)|^\/institutions(\/|$)|^\/campuses(\/|$)|^\/subdomain)/.test(url);
        if (!skip) {
          config.params = config.params || {};
          if (scope.institutionId && config.params.institution_id === undefined) {
            config.params.institution_id = scope.institutionId;
          }
          if (scope.campusId && config.params.campus_id === undefined) {
            config.params.campus_id = scope.campusId;
          }
        }
      }
    }
  } catch { /* scope injection is best-effort — never block a request */ }
  return config;
});

let isRefreshing = false;
let queue = [];

const flush = (err) => {
  queue.forEach((p) => (err ? p.reject(err) : p.resolve()));
  queue = [];
};

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    // #26: friendly UX for rate-limit responses. Sonner toast surfaces the
    // server's message (e.g. "Too many requests. Please try again shortly.")
    // instead of a generic red error box. Never break the caller's own error
    // handling — just add the toast on top.
    if (error?.response?.status === 429) {
      try {
        const { toast } = await import("sonner");
        const msg = error.response?.data?.detail || "Too many requests — please try again in a bit.";
        toast.error(msg, { id: `rate-limit-${error.config?.url || "req"}` });
      } catch { /* toast is optional */ }
    }
    const original = error.config;
    if (
      error.response &&
      error.response.status === 401 &&
      !original._retry &&
      !original.url?.includes("/auth/login") &&
      !original.url?.includes("/auth/refresh") &&
      !original.url?.includes("/auth/me")
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          queue.push({ resolve, reject });
        }).then(() => api(original));
      }
      original._retry = true;
      isRefreshing = true;
      try {
        const refreshTok = localStorage.getItem("refresh_token");
        const headers = refreshTok ? { Authorization: `Bearer ${refreshTok}` } : {};
        const { data } = await api.post("/auth/refresh", {}, { headers });
        if (data?.access_token) localStorage.setItem("access_token", data.access_token);
        if (data?.refresh_token) localStorage.setItem("refresh_token", data.refresh_token);
        flush(null);
        if (original.headers) original.headers.Authorization = `Bearer ${data.access_token}`;
        return api(original);
      } catch (e) {
        flush(e);
        localStorage.removeItem("access_token");
        localStorage.removeItem("refresh_token");
        return Promise.reject(e);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(error);
  }
);

/**
 * formatApiErrorDetail — turn any API error shape into a human-readable string.
 *
 * Accepts EITHER:
 *   • a detail value (string, list, object) as returned by FastAPI
 *   • OR a full axios error (with `.response`, `.request`, `.message`)
 *
 * The old callsite pattern `formatApiErrorDetail(e.response?.data?.detail)`
 * still works. New callsites can pass `e` directly to get richer output
 * (HTTP status + method + URL) when the backend returned no useful detail.
 */
export function formatApiErrorDetail(input) {
  // 1. Full axios error object → extract detail + status + URL context
  if (input && typeof input === "object" && (input.isAxiosError || input.response || input.request || input.config)) {
    const err = input;
    const status = err.response?.status;
    const method = (err.config?.method || "").toUpperCase();
    const url = err.config?.url || "";
    const data = err.response?.data;

    // Prefer FastAPI's `detail` field
    const detailStr = _detailToString(data?.detail);
    if (detailStr) {
      // For 5xx surface method + URL so devs can trace immediately
      if (status && status >= 500) return `${detailStr} (${method} ${url} → ${status})`;
      return detailStr;
    }
    // Non-detail error bodies (some middlewares use `message`, `error`, `errmsg`)
    const alt = data?.message || data?.error || data?.errmsg;
    if (alt) return `${alt}${status ? ` (HTTP ${status})` : ""}`;

    // Body is empty or HTML — fall back to status text + URL
    if (status) {
      const statusText = err.response?.statusText || "Server error";
      return `${statusText} (${method} ${url} → ${status})`;
    }
    // No response at all → network / CORS / offline
    if (err.request) return "Network error — the request never reached the server. Check your connection.";
    return err.message || "Something went wrong. Please try again.";
  }

  // 2. Detail value only (legacy callsite)
  return _detailToString(input) || "Something went wrong. Please try again.";
}

/** Convert a FastAPI `detail` (string | list | object) to a plain string.
 *  Returns null if there's nothing useful. */
function _detailToString(detail) {
  if (detail == null) return null;
  if (typeof detail === "string") {
    // FastAPI's default 500 handler returns "Internal Server Error" which is
    // useless for debugging. Treat it as "no detail".
    if (detail === "Internal Server Error") return null;
    return detail;
  }
  if (Array.isArray(detail)) {
    const parts = detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : typeof e === "string" ? e : null))
      .filter(Boolean);
    return parts.length ? parts.join(" · ") : null;
  }
  if (detail && typeof detail === "object") {
    if (typeof detail.msg === "string") return detail.msg;
    if (typeof detail.message === "string") return detail.message;
    // Last resort: JSON string, but keep it short
    const s = JSON.stringify(detail);
    return s.length > 200 ? s.slice(0, 197) + "…" : s;
  }
  return String(detail);
}
